/*
 * The words for the who's-off post in a Slack channel — Slack build step 4 (DESIGN-slack.md).
 *
 * Pure, so `slackDigest.checks.mjs` can run them without a browser. Used by
 * `components/DigestSlackRow.jsx` (Settings → Notifications, where the channel is chosen —
 * Rob's A1) and by the one line on `SlackIntegrationCard.jsx`.
 */

const KIND_WORDS = { daily: 'daily', weekly: 'weekly', monthly: 'monthly' };

/* "#leave" or "🔒 leave-private". The padlock is how Slack itself marks a private channel. */
export function channelLabel(channel) {
  if (!channel) return '';
  const name = channel.name || channel.id || 'unnamed channel';
  return channel.private ? `🔒 ${name}` : `#${name}`;
}

/*
 * Why the last post didn't go, as the end of a sentence. The server stores Slack's own code
 * (`slack_workspaces.digest_error`); this is the only place it becomes words.
 *
 * ⚠️ Each one names the fix where there is one (Rob's D1). An unknown code still says
 * something true rather than nothing.
 */
export function postProblemText(code) {
  switch (code) {
    case 'channel_not_found':
      return 'Slack can’t find that channel any more. It may have been deleted, or HolidayHive was removed from it';
    case 'is_archived':
      return 'the channel has been archived';
    case 'not_in_channel':
      return 'HolidayHive isn’t in that channel. In Slack, type /invite @HolidayHive in it';
    case 'restricted_action':
    case 'restricted_action_read_only_channel':
      return 'only some people can post in that channel (a Slack setting). Choose another channel, or change who can post there in Slack';
    case 'restricted_action_thread_only_channel':
      return 'that channel only allows replies in threads. Choose another channel';
    case 'ratelimited':
    case 'slack_unreachable':
    case 'slack_unavailable':
      return 'Slack couldn’t be reached just then';
    case 'message_limit_exceeded':
      return 'your Slack workspace has reached its message limit for now';
    default:
      return code ? `Slack said “${code}”` : 'Slack didn’t say why';
  }
}

/* "daily", "daily and weekly", "daily, weekly and monthly". */
export function kindsList(kinds) {
  const words = (kinds || []).map((k) => KIND_WORDS[k] || k);
  if (words.length <= 1) return words.join('');
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}

/*
 * The one line on the Slack card in Settings → Integrations. The channel is CHOSEN on the
 * Notifications tab; this only says where things stand and points there.
 *
 * ⚠️ "Posting" means a digest whose Slack switch is on AND which is itself on — the server
 * works that out (`routers/slack.digest_summary`), so a switch with its digest off is never
 * reported as posting (Rob's decision (a)).
 */
export function cardLine(digest) {
  if (!digest || !digest.channel_id) {
    return { text: 'Who’s off isn’t posted in a Slack channel yet.', link: 'Choose one in Notifications' };
  }
  const where = channelLabel({ id: digest.channel_id, name: digest.channel_name, private: digest.private });
  if (!digest.posting || digest.posting.length === 0) {
    return { text: `Who’s off channel: ${where}, but no digest is set to post there yet.`,
             link: 'Turn one on in Notifications' };
  }
  const many = digest.posting.length > 1;
  return { text: `Who’s off: the ${kindsList(digest.posting)} digest${many ? 's post' : ' posts'} in ${where}.`,
           link: 'Change it in Notifications' };
}

/* What the test button's toast says about Slack, from the server's one word. */
export function testToastSlack(word) {
  switch (word) {
    case 'posted': return 'Posted in Slack too.';
    case 'digest_off': return 'This digest is switched off, so nothing was posted in Slack.';
    case 'no_channel': return 'Slack is switched on but no channel is chosen, so nothing was posted there.';
    case 'not_connected': return 'Slack is switched on but not connected, so nothing was posted there.';
    case 'failed': return 'Slack refused the post — the reason is shown under the Slack channel.';
    default: return '';
  }
}
